import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, LogOut, List, Plus } from 'lucide-react';
import { API_BASE, apiFetch, getToken, getEmail, setSession, clearSession } from './api';
import { Spinner, ErrorBanner } from './ui';
import LinkCard from './LinkCard';
import LinksPage from './LinksPage';

import logoLight from './assets/logo_light.png';
import logoDark from './assets/logo_dark.png';

const Logo = ({ theme }) => (
    <img
        src={theme === 'light' ? logoLight : logoDark}
        alt="ASA URL Shortener Logo"
        className="h-20 w-auto mx-auto"
    />
);

const inputClass = 'w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200';
const submitClass = 'bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-12 rounded-lg flex items-center justify-center transition duration-200 disabled:bg-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed';

// focus-visible, not focus: keyboard users get a ring, mouse users don't get one
// flashing on every click.
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900';

// 44px minimum on phones per touch-target guidance, tightened once there is a
// pointer. No radius here — callers set it, because two competing rounded-*
// classes resolve by stylesheet order, not by order in the class attribute.
const navPill = ({ isActive }) =>
    `inline-flex items-center gap-2 rounded-lg px-3 min-h-[44px] sm:min-h-[38px] text-sm font-medium no-underline cursor-pointer transition-colors duration-200 ${focusRing} ${
        isActive
            // The only tinted element in the bar, so "where am I" reads instantly.
            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
    }`;

const iconAction = `inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-[38px] sm:min-w-[38px] px-2 text-sm font-medium cursor-pointer text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white transition-colors duration-200 ${focusRing}`;

function RequireAuth({ children }) {
    const location = useLocation();
    if (!getToken()) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    return children;
}

function Layout({ children }) {
    const [theme, setTheme] = useState(() => {
        if (typeof window !== 'undefined') {
            let savedTheme = null;
            try {
                savedTheme = localStorage.getItem('theme');
            } catch {
                /* private window with storage blocked */
            }
            if (savedTheme) return savedTheme;
            return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'light';
    });
    const navigate = useNavigate();
    const location = useLocation();
    const email = getEmail();

    useEffect(() => {
        if (theme === 'dark') document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
        try {
            localStorage.setItem('theme', theme);
        } catch {
            /* preference just won't persist */
        }
    }, [theme]);

    const signOut = async () => {
        try {
            await apiFetch('/api/auth/logout', { method: 'POST' });
        } catch {
            // Revoke locally regardless — the token is useless to us either way.
        }
        clearSession();
        navigate('/login', { replace: true });
    };

    const isSignedIn = Boolean(email) && location.pathname !== '/login';

    return (
        <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col items-center font-sans p-4">
            <nav aria-label="Main" className="w-full max-w-2xl flex items-center gap-2 pt-2">
                {isSignedIn && (
                    // Destinations sit left and carry the active state; account
                    // actions are pushed right, so the two never read as one row
                    // of equally-weighted links.
                    <div className="flex items-center gap-1">
                        <NavLink to="/" end className={navPill}>
                            <Plus size={16} aria-hidden="true" />
                            <span>Create</span>
                        </NavLink>
                        <NavLink to="/links" className={navPill}>
                            <List size={16} aria-hidden="true" />
                            <span>My links</span>
                        </NavLink>
                    </div>
                )}

                <div className="ml-auto flex items-center gap-1 sm:gap-2">
                    {isSignedIn && (
                        <>
                            <span
                                className="hidden sm:inline max-w-[14rem] truncate text-sm text-gray-500 dark:text-gray-400"
                                title={email}
                            >
                                {email}
                            </span>
                            <span className="hidden sm:block h-6 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                            <button onClick={signOut} className={`${iconAction} rounded-lg sm:px-3`}>
                                <LogOut size={16} aria-hidden="true" />
                                {/* Label collapses on phones; the button keeps its
                                    accessible name either way. */}
                                <span className="sr-only sm:not-sr-only sm:ml-2">Sign out</span>
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
                        className={`${iconAction} rounded-full`}
                        aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
                    >
                        {theme === 'light' ? <Moon size={18} aria-hidden="true" /> : <Sun size={18} aria-hidden="true" />}
                    </button>
                </div>
            </nav>

            <div className="w-full max-w-2xl mx-auto flex-1 flex flex-col justify-center py-8">
                <div className="text-center mb-10"><Logo theme={theme} /></div>
                {children}
            </div>

            <footer className="text-center mt-auto py-6 text-gray-500 dark:text-gray-400">
                <p>© 2025 Asa Kreasi Interasia.</p>
            </footer>
        </div>
    );
}

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    if (getToken()) return <Navigate to="/" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            // Raw fetch, not apiFetch: a wrong password is a 401, and apiFetch
            // turns every 401 into "session expired" plus a redirect.
            const response = await fetch(`${API_BASE}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || 'Something went wrong');
            setSession(data.token, data.user.email);
            navigate(location.state?.from || '/', { replace: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white text-center mb-8">Sign in</h1>

            <form onSubmit={handleSubmit} className="space-y-4">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    autoComplete="username"
                    className={inputClass}
                    disabled={isLoading}
                />
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                    className={inputClass}
                    disabled={isLoading}
                />
                <div className="pt-4 flex justify-center">
                    <button type="submit" className={submitClass} disabled={isLoading || !email || !password}>
                        {isLoading ? <Spinner /> : 'Sign in'}
                    </button>
                </div>
            </form>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
                Accounts are created by an administrator.
            </p>

            <div className="mt-6 min-h-[60px]"><ErrorBanner message={error} /></div>
        </>
    );
}

function CreatePage() {
    const [longUrl, setLongUrl] = useState('');
    const [customCode, setCustomCode] = useState('');
    const [shortUrl, setShortUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setShortUrl('');

        if (!longUrl) {
            setError('Please enter a URL to shorten.');
            return;
        }

        setIsLoading(true);
        try {
            const data = await apiFetch('/api/shorten', {
                method: 'POST',
                body: JSON.stringify({ longUrl, customCode }),
            });
            setShortUrl(data.shortUrl);
            setCustomCode('');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white my-4 text-center">
                Shorten the <span className="text-indigo-600">link</span>. Strengthen the <span className="text-indigo-600">brand</span>.
            </h1>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <input
                        type="url"
                        value={longUrl}
                        onChange={(e) => setLongUrl(e.target.value)}
                        placeholder="Enter your long URL"
                        className={inputClass}
                        disabled={isLoading}
                    />
                </div>

                <div className="flex items-center space-x-2">
                    <span className="text-gray-500 dark:text-gray-400 font-medium p-3">
                        link.asakreasi.com/
                    </span>
                    <input
                        type="text"
                        value={customCode}
                        onChange={(e) => setCustomCode(e.target.value)}
                        placeholder="Enter your short URL"
                        className={inputClass}
                        disabled={isLoading}
                    />
                </div>

                <div className="pt-4 flex justify-center">
                    <button type="submit" className={submitClass} disabled={isLoading || !longUrl}>
                        {isLoading ? <Spinner /> : 'Shorten!'}
                    </button>
                </div>
            </form>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
                By using ASA URL Shortener, you agree to our <a href="#" className="text-indigo-600 hover:underline">Terms of Service</a>.
            </p>

            <div className="mt-6 min-h-[90px]">
                <ErrorBanner message={error} />
                {shortUrl && <LinkCard shortUrl={shortUrl} onError={setError} />}
            </div>
        </>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <Layout>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/" element={<RequireAuth><CreatePage /></RequireAuth>} />
                    <Route path="/links" element={<RequireAuth><LinksPage /></RequireAuth>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Layout>
        </BrowserRouter>
    );
}
